/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */

import { PackagesCartItem } from 'types/entities/PackagesCartItem'
import SellerPackageCartItemRow from 'pages/Front/Booking/Seller/components/SellerPackageCartItemRow'
import { useTranslation } from 'react-i18next'
import { Reservation } from 'types/entities/Reservation'
import clsx from 'clsx'
import {
    BanknotesIcon,
    CircleStackIcon,
    Cog6ToothIcon,
    CurrencyDollarIcon,
    PercentBadgeIcon,
    TagIcon,
} from '@heroicons/react/24/outline'
import {
    UiTable,
    UiTableBody,
    UiTableCell,
    UiTableHead,
    UiTableHeaderCell,
    UiTableRow,
} from 'components/Ui/UiTable'

interface BookingSellerPackagesCartItemsTableProps {
    packagesCartItems: PackagesCartItem[]
    reservation: Reservation
    title: string
    isAccessTicket?: boolean
}

const BookingSellerPackagesCartItemsTable = (
    props: BookingSellerPackagesCartItemsTableProps
) => {
    const { t } = useTranslation()

    return (
        <div
            className={clsx(
                'BookingSellerPackagesCartItemsTable',
                'UiCard overflow-hidden flex flex-col'
            )}
        >
            <div
                className={clsx(
                    'BookingSellerPackagesCartItemsTableHeader',
                    'px-4 py-2 flex items-center justify-between border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shrink-0'
                )}
            >
                <h2
                    className={clsx(
                        'font-bold text-lg text-gray-900 dark:text-gray-100'
                    )}
                >
                    {props.title}
                </h2>
            </div>
            <div className="flex-1 overflow-x-auto min-h-[100px]">
                <UiTable className={clsx('w-full')}>
                    <UiTableHead>
                        <UiTableHeaderCell className="py-2.5">
                            <div
                                className={clsx(
                                    'flex items-center text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap'
                                )}
                            >
                                <TagIcon
                                    className={clsx('w-3.5 h-3.5 mr-1.5')}
                                />
                                <span>{t('Item')}</span>
                            </div>
                        </UiTableHeaderCell>
                        <UiTableHeaderCell className="py-2.5">
                            <div
                                className={clsx(
                                    'flex items-center text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap'
                                )}
                            >
                                <CircleStackIcon
                                    className={clsx('w-3.5 h-3.5 mr-1.5')}
                                />
                                <span>{t('Quantity')}</span>
                            </div>
                        </UiTableHeaderCell>
                        {!props.isAccessTicket && (
                            <UiTableHeaderCell className="py-2.5">
                                <div
                                    className={clsx(
                                        'flex items-center text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap'
                                    )}
                                >
                                    <Cog6ToothIcon
                                        className={clsx('w-3.5 h-3.5 mr-1.5')}
                                    />
                                    <span>{t('Actions')}</span>
                                </div>
                            </UiTableHeaderCell>
                        )}
                        <UiTableHeaderCell className="py-2.5">
                            <div
                                className={clsx(
                                    'flex items-center text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap'
                                )}
                            >
                                <CurrencyDollarIcon
                                    className={clsx('w-3.5 h-3.5 mr-1.5')}
                                />
                                <span>{t('Unit price')}</span>
                            </div>
                        </UiTableHeaderCell>
                        <UiTableHeaderCell className="py-2.5">
                            <div
                                className={clsx(
                                    'flex items-center text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap'
                                )}
                            >
                                <CurrencyDollarIcon
                                    className={clsx('w-3.5 h-3.5 mr-1.5')}
                                />
                                <div className="flex flex-col leading-tight">
                                    <span>{t('Unit price')}</span>
                                    <span className="text-[9px] opacity-75">
                                        {t('with discount')}
                                    </span>
                                </div>
                            </div>
                        </UiTableHeaderCell>
                        <UiTableHeaderCell className="py-2.5">
                            <div
                                className={clsx(
                                    'flex items-center text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap'
                                )}
                            >
                                <PercentBadgeIcon
                                    className={clsx('w-3.5 h-3.5 mr-1.5')}
                                />
                                <span>{t('Discount')}</span>
                            </div>
                        </UiTableHeaderCell>
                        <UiTableHeaderCell className="py-2.5">
                            <div
                                className={clsx(
                                    'flex items-center justify-end text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap'
                                )}
                            >
                                <BanknotesIcon
                                    className={clsx('w-3.5 h-3.5 mr-1.5')}
                                />
                                <span>{t('Price')}</span>
                            </div>
                        </UiTableHeaderCell>
                    </UiTableHead>
                    <UiTableBody>
                        {props.packagesCartItems.length > 0 ? (
                            [...props.packagesCartItems]
                                .sort((a, b) =>
                                    (a.packageItem?.article_id ?? 0) >
                                    (b.packageItem?.article_id ?? 0)
                                        ? 1
                                        : -1
                                )
                                .map((packagesCartItem, index) => (
                                    <SellerPackageCartItemRow
                                        packagesCartItem={packagesCartItem}
                                        isAccessTicket={props.isAccessTicket}
                                        reservation={props.reservation}
                                        key={packagesCartItem.id || index}
                                    />
                                ))
                        ) : (
                            <UiTableRow>
                                <UiTableCell
                                    className={clsx(
                                        'px-3 py-6 text-center text-gray-400 italic'
                                    )}
                                    colSpan={props.isAccessTicket ? 6 : 7}
                                >
                                    {t('No items found.')}
                                </UiTableCell>
                            </UiTableRow>
                        )}
                    </UiTableBody>
                </UiTable>
            </div>
        </div>
    )
}

export default BookingSellerPackagesCartItemsTable
